import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faMagnifyingGlass, faFilter, faBookmark, faChevronLeft, faChevronRight, } from '@fortawesome/free-solid-svg-icons';
const SLIDES = [
    {
        icon: faSearch,
        title: 'Find any swimmer',
        body: "Search by surname to pull up a swimmer's full competition history, personal bests and rankings.",
    },
    {
        icon: faMagnifyingGlass,
        title: 'Try a quick example',
        body: 'Not sure where to start? Tap a name to load it into the search box above.',
        examples: ['Peaty', 'Guy', 'Evans', 'Dean', 'Anderson', 'Colbert', 'Bird', 'Greenbank', 'Morgan'],
    },
    {
        icon: faFilter,
        title: 'Narrow it down',
        body: 'Once results load, use the filter box to search instantly by name or club.',
    },
    {
        icon: faBookmark,
        title: 'Save your favourites',
        body: 'Tap the save icon on any swimmer to bookmark them for quick access later.',
    },
];
const AUTOPLAY_MS = 6000;
const SearchHelpCarousel = ({ onTryExample }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const touchStartX = useRef(null);
    const goTo = useCallback((index) => {
        const total = SLIDES.length;
        setActiveIndex(((index % total) + total) % total);
    }, []);
    const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
    const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);
    useEffect(() => {
        if (isPaused)
            return;
        const timer = setInterval(() => {
            setActiveIndex(prev => (prev + 1) % SLIDES.length);
        }, AUTOPLAY_MS);
        return () => clearInterval(timer);
    }, [isPaused, activeIndex]);
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        if (touchStartX.current === null)
            return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        const SWIPE_THRESHOLD = 40;
        if (delta > SWIPE_THRESHOLD)
            goPrev();
        else if (delta < -SWIPE_THRESHOLD)
            goNext();
        touchStartX.current = null;
    };
    return (React.createElement("div", { className: "search-carousel", onMouseEnter: () => setIsPaused(true), onMouseLeave: () => setIsPaused(false), onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd },
        React.createElement("button", { type: "button", className: "search-carousel-nav search-carousel-nav-prev", onClick: goPrev, "aria-label": "Previous tip" },
            React.createElement(FontAwesomeIcon, { icon: faChevronLeft })),
        React.createElement("div", { className: "search-carousel-viewport" },
            React.createElement("div", { className: "search-carousel-track", style: { transform: `translateX(-${activeIndex * 100}%)` } }, SLIDES.map((slide, index) => (React.createElement("div", { className: "search-carousel-slide", key: slide.title, "aria-hidden": index !== activeIndex },
                React.createElement("div", { className: "search-carousel-icon" },
                    React.createElement(FontAwesomeIcon, { icon: slide.icon })),
                React.createElement("h3", null, slide.title),
                React.createElement("p", null, slide.body),
                slide.examples && (React.createElement("div", { className: "search-carousel-examples" }, slide.examples.map(name => (React.createElement("button", { type: "button", key: name, className: "search-carousel-example-chip", onClick: () => onTryExample(name) }, name)))))))))),
        React.createElement("button", { type: "button", className: "search-carousel-nav search-carousel-nav-next", onClick: goNext, "aria-label": "Next tip" },
            React.createElement(FontAwesomeIcon, { icon: faChevronRight })),
        React.createElement("div", { className: "search-carousel-dots", role: "tablist", "aria-label": "Search tips" }, SLIDES.map((slide, index) => (React.createElement("button", { type: "button", key: slide.title, role: "tab", "aria-selected": index === activeIndex, "aria-label": `Go to tip ${index + 1}`, className: `search-carousel-dot${index === activeIndex ? ' active' : ''}`, onClick: () => goTo(index) }))))));
};
export default SearchHelpCarousel;
